import { useCallback, useEffect, useMemo, useState } from "react";
import { ACTIONS, EVENTS, STATUS } from "react-joyride";
import { useDispatch } from "react-redux";
import { tourEmissionFromDashboardStart } from "../../features/tour/tourSlice";

export const menuName = "scope1&2Create";

// Base steps factory
const ALL_STEPS = ({ t, company, location, source, monthAndYear, assetName, type, unit, amount, attachment, save }) => ([
    { content: company.second, spotlightClicks: true, disableBeacon: true, target: "#tour__company", title: company.first },
    { spotlightClicks: true, target: "#tour__location", title: location },
    { spotlightClicks: true, target: "#tour__month_and_year", title: monthAndYear },
    { content: source.second, spotlightClicks: true, target: "#tour__emission_source", title: source.first },
    { spotlightClicks: true, target: "#tour__asset_name", title: assetName },
    { spotlightClicks: true, target: "#tour__type", title: type },
    { spotlightClicks: true, target: "#tour__unit", title: unit },
    { spotlightClicks: true, target: "#tour__amount", title: amount },
    { spotlightClicks: true, target: "#tour__attachment", title: attachment },
    { target: "#tour__save", title: save },
]);

/**
 * Build base (i18n-aware) steps
 */
export function buildInitialSteps(t) {
    return ALL_STEPS({
        t,
        company: { first: t("emission.tour.company1"), second: t("emission.tour.company2") },
        location: t("emission.tour.location"),
        source: { first: t("emission.tour.source1"), second: t("emission.tour.source2") },
        monthAndYear: t("emission.tour.month"),
        assetName: t("emission.tour.asset"),
        type: t("emission.tour.fuel"),
        unit: t("emission.tour.unit.fuel"),
        amount: t("emission.tour.amount"),
        attachment: t("emission.tour.attachment.button"),
        save: t("emission.tour.save"),
    });
}

/**
 * Derive steps based on selected category + available emission factors
 */
export function deriveSteps({ t, selectedCategory, listEmissionFactor }) {
    const base = buildInitialSteps(t);

    if (!selectedCategory) return base;

    const startIndex = 5; // where "type" sits in base
    let typeAtStartIndex = base[startIndex].title;
    let unitAfterStartIndex = base[startIndex + 1].title;

    if (Number(selectedCategory) === 3) {
        typeAtStartIndex = t("emission.tour.refrigerant");
        unitAfterStartIndex = t("emission.tour.unit.refrigerant");
    } else if (Number(selectedCategory) === 4) {
        typeAtStartIndex = t("emission.tour.treatmentType");
        unitAfterStartIndex = t("emission.tour.unit.treatmentType");
    } else if (Number(selectedCategory) === 5) {
        typeAtStartIndex = t("emission.tour.wasteType");
        unitAfterStartIndex = t("emission.tour.unit.wasteType");
    } else if (Number(selectedCategory) === 6) {
        typeAtStartIndex = t("emission.tour.processType");
        unitAfterStartIndex = t("emission.tour.unit.processType");    
    } else if (Number(selectedCategory) === 7) {
        typeAtStartIndex = t("emission.tour.grid");
        unitAfterStartIndex = t("emission.tour.unit.grid");
    } else if (Number(selectedCategory) === 12) {
        typeAtStartIndex = t("emission.tour.explosives");
        unitAfterStartIndex = t("emission.tour.unit.explosives");
    } else if (Number(selectedCategory) === 13) {
        typeAtStartIndex = t("emission.tour.landType");
        unitAfterStartIndex = t("emission.tour.unit.landType");
    } else if (Number(selectedCategory) === 18) {
        typeAtStartIndex = t("emission.tour.fireExtinguisher");
        unitAfterStartIndex = t("emission.tour.unit.fireExtinguisher");
    } else if ([20, 21, 22].includes(Number(selectedCategory))) {
        typeAtStartIndex = t("emission.tour.gasType");
        unitAfterStartIndex = t("emission.tour.unit.gasType");
    } else if (Number(selectedCategory) === 25) {
        typeAtStartIndex = t("emission.tour.mineDepth");
        unitAfterStartIndex = t("emission.tour.unit.cmm");
    } else if (Number(selectedCategory) === 26) {
        typeAtStartIndex = t("emission.tour.treatmentType");
        unitAfterStartIndex = t("emission.tour.unit.volumeOfWastewater");
    }

    if (Number(selectedCategory) === 26) {
        // replace 2 steps with 5 (type, inlet, outlet, unit, amount)
        return base.toSpliced(
            startIndex,
            3,
            { spotlightClicks: true, target: "#tour__type", title: typeAtStartIndex },
            { spotlightClicks: true, target: "#tour__inletCOD", title: t("emission.tour.enterInletCOD") },
            { spotlightClicks: true,  target: "#tour__outletCOD", title: t("emission.tour.enterOutletCOD") },
            { spotlightClicks: true, target: "#tour__unit", title: unitAfterStartIndex },
            { spotlightClicks: true, target: "#tour__amount", title: t("emission.tour.enterTheVolumeAmount") },
        );
    }

    if (listEmissionFactor.length > 0) {
        return base.toSpliced(
            startIndex,
            2,
            { spotlightClicks: true, target: "#tour__type", title: typeAtStartIndex },
            { spotlightClicks: true, target: "#tour__unit", title: unitAfterStartIndex },
        );
    }

    // No emission factors for this category -> remove the two steps
    return base.toSpliced(startIndex, 2);
}

/**
 * Hook that owns all state & callbacks for the tour on the Create Emission page
 */
export function useEmissionCreateTour({
                                          t,
                                          account,
                                          selectedCategory,
                                          listEmissionFactor,
                                          tourFromDashboardRun,
                                      }) {
    const dispatch = useDispatch();

    const [tourRun, setTourRun] = useState(false);
    const [tourStepIndex, setTourStepIndex] = useState(0);

    // Build steps and recompute when inputs change
    const tourSteps = useMemo(
        () => deriveSteps({ t, selectedCategory, listEmissionFactor }),
        [t, selectedCategory, listEmissionFactor],
    );

    // First mount: check if user has taken this tour
    useEffect(() => {
        if (!Array.isArray(account?.tours) || !account.tours.includes(menuName)) {
            setTourRun(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // External trigger (from dashboard button)
    useEffect(() => {
        if (tourFromDashboardRun) {
            setTourRun(true);
        }
    }, [tourFromDashboardRun]);

    const handleEnd = useCallback(() => {
        setTourRun(false);
        setTourStepIndex(0);
    }, []);

    const handleJoyrideCallback = useCallback(
        (data) => {
            const { action, index, status, type } = data;
            if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
                setTourRun(false);
                setTourStepIndex(0);
                dispatch(tourEmissionFromDashboardStart(false));
            } else if (type === EVENTS.TARGET_NOT_FOUND) {
                setTourStepIndex(index - 1);
            } else if (type === EVENTS.STEP_AFTER) {
                const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
                setTourStepIndex(nextStepIndex);
            }
        },
        [dispatch],
    );

    return {
        tourSteps,
        tourRun,
        setTourRun,
        tourStepIndex,
        handleEnd,
        handleJoyrideCallback,
    };
}
